import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useSlotGroup,
  EditableSlotGroup,
  EditableCollectionBinding,
} from '@vibesites/runtime/react';
import { useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { VibesitesImage } from '@vibesites/ui-primitives';

interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_image_url?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
  floorplan_baths_min?: string | null;
  floorplan_sqft_min?: string | null;
  floorplan_sqft_max?: string | null;
  floorplan_price_min?: string | null;
  floorplan_price_max?: string | null;
  floorplan_available_units?: string | null;
}

interface GalleryPhoto {
  image?: string | null;
  original_url?: string | null;
  title?: string | null;
  alt_text?: string | null;
  description?: string | null;
}

function photoSrc(photo: GalleryPhoto): string {
  return photo.image || photo.original_url || '';
}

/** Drop bathroom shots that Multihub still tags as Apartment. */
function isBathroomPhoto(photo: GalleryPhoto): boolean {
  const hay = `${photo.alt_text || ''} ${photo.description || ''}`.toLowerCase();
  return /\bbathrooms?\b|\bwashrooms?\b/.test(hay);
}

// ============================================================================
// Bedroom category helpers
// ============================================================================

interface BedroomCategory {
  key: string;
  /** Large framed glyph — "S", "1", "2", … */
  glyph: string;
  /** Small framed caption — "STUDIO", "BEDROOM", "BEDROOMS" */
  caption: string;
  param: string;
}

const BED_ORDER = ['studio', 'one', 'two', 'three', 'four', 'five'];
/** Bedroom category cards, and the apartment photos those cards display. */
export const FLOORPLAN_CATEGORY_CARD_LIMIT = 4;
const MAX_CARDS = FLOORPLAN_CATEGORY_CARD_LIMIT;

function bedCountToKey(bedsRaw: string | null | undefined): string {
  if (!bedsRaw) return 'studio';
  const trimmed = bedsRaw.trim().toLowerCase();
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 'studio';
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num <= 0) return 'studio';
  const names = ['studio', 'one', 'two', 'three', 'four', 'five'];
  return names[num] || `${num}`;
}

function keyToGlyph(key: string): string {
  if (key === 'studio') return 'S';
  const idx = BED_ORDER.indexOf(key);
  if (idx > 0) return String(idx);
  return key;
}

function keyToCaption(key: string): string {
  if (key === 'studio') return 'STUDIO';
  if (key === 'one') return 'BEDROOM';
  return 'BEDROOMS';
}

function groupFloorplansByBeds(
  floorplans: readonly { floorplan_beds_min?: string | null }[],
): BedroomCategory[] {
  const groups = new Set<string>();

  for (const plan of floorplans) {
    groups.add(bedCountToKey(plan.floorplan_beds_min));
  }

  const sorted = [...groups].sort((a, b) => {
    const ai = BED_ORDER.indexOf(a);
    const bi = BED_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return sorted.map((key) => ({
    key,
    glyph: keyToGlyph(key),
    caption: keyToCaption(key),
    param: key,
  }));
}

/**
 * Apartment photos actually painted on the bedroom category cards.
 * Same rules as the card grid: non-bathroom shots from the Apartment gallery,
 * one per bedroom type, capped at {@link FLOORPLAN_CATEGORY_CARD_LIMIT}.
 */
export function selectCategoryCardPhotos<T extends GalleryPhoto>(
  apartmentPhotos: readonly T[] | null | undefined,
  floorplans: readonly { floorplan_beds_min?: string | null }[] | null | undefined,
): T[] {
  const cardCount = Math.min(
    groupFloorplansByBeds(floorplans ?? []).length,
    FLOORPLAN_CATEGORY_CARD_LIMIT,
  );
  return (apartmentPhotos ?? [])
    .filter((photo) => photoSrc(photo) && !isBathroomPhoto(photo))
    .slice(0, cardCount);
}

// ============================================================================
// One slot group per bedroom category CTA (positional: card 1 = first type)
// ============================================================================

const categoryCta1Def: SlotGroupDefinition = {
  label: 'Floor Plan CTA 1',
  description:
    'First bedroom card. Leave fields blank to use the bedroom type defaults. Card photos come from Apartment Photos.',
  fields: {
    glyph: {
      label: 'Glyph',
      type: 'text',
      defaultValue: '',
      description: 'Large framed letter/number. Leave blank for the bedroom type default (S, 1, 2…).',
    },
    caption: {
      label: 'Caption',
      type: 'text',
      defaultValue: '',
      description: 'Small framed label. Leave blank for STUDIO / BEDROOM / BEDROOMS.',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '',
      description: 'Leave blank to link to the matching bedroom filter on Floor Plans.',
    },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const categoryCta2Def: SlotGroupDefinition = {
  label: 'Floor Plan CTA 2',
  description:
    'Second bedroom card. Leave fields blank to use the bedroom type defaults. Card photos come from Apartment Photos.',
  fields: {
    glyph: {
      label: 'Glyph',
      type: 'text',
      defaultValue: '',
      description: 'Large framed letter/number. Leave blank for the bedroom type default (S, 1, 2…).',
    },
    caption: {
      label: 'Caption',
      type: 'text',
      defaultValue: '',
      description: 'Small framed label. Leave blank for STUDIO / BEDROOM / BEDROOMS.',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '',
      description: 'Leave blank to link to the matching bedroom filter on Floor Plans.',
    },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const categoryCta3Def: SlotGroupDefinition = {
  label: 'Floor Plan CTA 3',
  description:
    'Third bedroom card. Leave fields blank to use the bedroom type defaults. Card photos come from Apartment Photos.',
  fields: {
    glyph: {
      label: 'Glyph',
      type: 'text',
      defaultValue: '',
      description: 'Large framed letter/number. Leave blank for the bedroom type default (S, 1, 2…).',
    },
    caption: {
      label: 'Caption',
      type: 'text',
      defaultValue: '',
      description: 'Small framed label. Leave blank for STUDIO / BEDROOM / BEDROOMS.',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '',
      description: 'Leave blank to link to the matching bedroom filter on Floor Plans.',
    },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const categoryCta4Def: SlotGroupDefinition = {
  label: 'Floor Plan CTA 4',
  description:
    'Fourth bedroom card. Leave fields blank to use the bedroom type defaults. Card photos come from Apartment Photos.',
  fields: {
    glyph: {
      label: 'Glyph',
      type: 'text',
      defaultValue: '',
      description: 'Large framed letter/number. Leave blank for the bedroom type default (S, 1, 2…).',
    },
    caption: {
      label: 'Caption',
      type: 'text',
      defaultValue: '',
      description: 'Small framed label. Leave blank for STUDIO / BEDROOM / BEDROOMS.',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '',
      description: 'Leave blank to link to the matching bedroom filter on Floor Plans.',
    },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const CATEGORY_CTA_DEFS = [
  { path: 'floorplanslist.category_cta_1', def: categoryCta1Def },
  { path: 'floorplanslist.category_cta_2', def: categoryCta2Def },
  { path: 'floorplanslist.category_cta_3', def: categoryCta3Def },
  { path: 'floorplanslist.category_cta_4', def: categoryCta4Def },
] as const;

// ============================================================================
// Category card
// ============================================================================

interface CategoryCardProps {
  category: BedroomCategory;
  image: string;
  alt: string;
  groupPath: string;
  definition: SlotGroupDefinition;
  columnCount: number;
}

function CategoryCard({
  category,
  image,
  alt,
  groupPath,
  definition,
  columnCount,
}: CategoryCardProps) {
  const cta = useSlotGroup(groupPath, definition);
  const glyph = (cta.glyph || '').trim() || category.glyph;
  const caption = (cta.caption || '').trim() || category.caption;
  const url = (cta.url || '').trim() || `/floor-plans?bedroom=${category.param}`;

  // 1-col <768, 2-col 768–1279, N-col from 1280 up (avoids compressed frames on small desktops)
  const sizes =
    columnCount <= 1
      ? '100vw'
      : `(max-width: 767px) 100vw, (max-width: 1279px) 50vw, ${Math.round(100 / columnCount)}vw`;

  return (
    <a
      data-reveal
      href={url}
      target={cta._target || '_self'}
      className="reveal-fade-up fp-card group relative block overflow-hidden cursor-pointer"
      aria-label={`View ${caption === 'STUDIO' ? 'Studio' : caption.toLowerCase()} floor plans`}
    >
      {image ? (
        <VibesitesImage
          url={image}
          alt={alt || `${caption} floor plans`}
          sizes={sizes}
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
      ) : (
        <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
      )}
      {/* Static veil — image stays still; only the label frame animates */}
      <div className="fp-card-overlay absolute inset-0 bg-black" aria-hidden="true" />
      <div className="fp-card-label-layer relative z-10 flex items-center justify-center h-full px-6">
        <EditableSlotGroup
          groupPath={groupPath}
          definition={definition}
          className="fp-card-frame flex flex-col items-center justify-center text-center text-white"
        >
          <span className="fp-card-glyph font-heading font-bold leading-none tracking-tight">
            {glyph}
          </span>
          <span className="fp-card-caption font-body font-bold uppercase mt-3">{caption}</span>
        </EditableSlotGroup>
      </div>
    </a>
  );
}

// ============================================================================
// Component
// ============================================================================

export function FloorplansList() {
  const floorplans = useCollectionBinding<Floorplan>('floorplanslist.items', {
    collection: 'floorplans',
  });
  const apartmentPhotos = useCollectionBinding<GalleryPhoto>('floorplanslist.apartment_images', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
    defaultLimit: MAX_CARDS,
  });
  const categories = useMemo(
    () => (floorplans?.length ? groupFloorplansByBeds(floorplans).slice(0, MAX_CARDS) : []),
    [floorplans],
  );

  const cardPhotos = useMemo(
    () => selectCategoryCardPhotos(apartmentPhotos, floorplans),
    [apartmentPhotos, floorplans],
  );

  useRevealStyles();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 120 });

  const hasCards = categories.length > 0;
  const columnCount = Math.max(categories.length, 1);

  return (
    <section className="w-full bg-[var(--vibe-color-background)]">
      <EditableCollectionBinding bindingPath="floorplanslist.items" label="Floor Plans">
        <EditableCollectionBinding
          bindingPath="floorplanslist.apartment_images"
          label="Apartment Photos"
        >
          {hasCards ? (
            <div
              ref={gridRef}
              className="fp-grid grid w-full"
              style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}
            >
              {categories.map((cat, i) => {
                const photo = cardPhotos[i];
                return (
                  <CategoryCard
                    key={cat.key}
                    category={cat}
                    image={photo ? photoSrc(photo) : ''}
                    alt={photo?.alt_text || photo?.title || ''}
                    groupPath={CATEGORY_CTA_DEFS[i].path}
                    definition={CATEGORY_CTA_DEFS[i].def}
                    columnCount={columnCount}
                  />
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-[var(--vibe-color-text)]/60">
              <p className="font-body text-lg">Floor plans will be displayed here.</p>
            </div>
          )}
        </EditableCollectionBinding>
      </EditableCollectionBinding>

      {/* Schema-lint anchors — literal groupPath required; never interactive */}
      <EditableSlotGroup
        groupPath="floorplanslist.category_cta_1"
        definition={categoryCta1Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="floorplanslist.category_cta_2"
        definition={categoryCta2Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="floorplanslist.category_cta_3"
        definition={categoryCta3Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="floorplanslist.category_cta_4"
        definition={categoryCta4Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        .fp-grid {
          gap: 0;
        }
        .fp-card {
          min-height: 420px;
          height: clamp(420px, 48vw, 560px);
          border-radius: 0;
        }
        .fp-card-overlay {
          opacity: 0.42;
          pointer-events: none;
        }
        /* Hover shrinks the framed glyph/caption (image stays static) */
        .fp-card-frame {
          container-type: inline-size;
          width: min(44%, 200px);
          aspect-ratio: 1 / 1;
          border: 4px solid rgba(255, 255, 255, 0.95);
          box-sizing: border-box;
          padding: clamp(0.85rem, 8cqi, 1.25rem);
          transform: scale(1);
          transition: transform 0.45s ease-in-out;
          will-change: transform;
        }
        .fp-card-glyph {
          font-size: clamp(2.5rem, 42cqi, 5.5rem);
        }
        .fp-card-caption {
          font-size: clamp(0.625rem, 8cqi, 0.8125rem);
          letter-spacing: 0.22em;
          white-space: nowrap;
        }
        .fp-card:hover .fp-card-frame,
        .fp-card:focus-visible .fp-card-frame {
          transform: scale(0.86);
        }
        /* Small / mid desktops: 2-col so frames aren't squeezed in a 4-up row */
        @media (max-width: 1279px) {
          .fp-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
          .fp-card {
            height: clamp(400px, 42vw, 520px);
            min-height: 400px;
          }
          .fp-card-frame {
            width: min(40%, 200px);
          }
        }
        @media (max-width: 767px) {
          .fp-grid {
            grid-template-columns: 1fr !important;
          }
          .fp-card {
            height: 360px;
            min-height: 360px;
          }
          .fp-card-frame {
            width: min(46%, 168px);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .fp-card-frame {
            transition: none;
          }
          .fp-card:hover .fp-card-frame,
          .fp-card:focus-visible .fp-card-frame {
            transform: none;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {},
  slotGroups: {
    'floorplanslist.category_cta_1': categoryCta1Def,
    'floorplanslist.category_cta_2': categoryCta2Def,
    'floorplanslist.category_cta_3': categoryCta3Def,
    'floorplanslist.category_cta_4': categoryCta4Def,
  },
  collectionBindings: {
    'floorplanslist.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
    'floorplanslist.apartment_images': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
      filter: { title: 'Apartment' },
      defaultLimit: 4,
      description:
        'Unit interior photos for bedroom category cards. Only Multihub gallery items titled Apartment; bathroom shots are excluded.',
    },
  },
};

(FloorplansList as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default FloorplansList;
